<template>
  <v-container
    class="pa-0"
    fluid
  >
    <v-row
      no-gutters
      style="overflow-y: auto;"
    >
      <v-col cols="12">
        <table
          class="divTable text-caption"
        >
          <tr class="divTableHeading">
            <th class="divTableHead text-left">
              #
            </th>

            <th class="divTableHead text-right">
              {{ $t('Payment.Table.Headers.Amount') }}
            </th>

            <th
              v-safe-html="$t('Payment.Table.Headers.PaymentType')"
              class="divTableHead text-center"
            />

            <template v-if="appHasFiscalDocsEnabled && appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD">
              <th class="divTableHead text-left">
                {{ $t('Payment.Table.Headers.Terminal') }}
              </th>

              <th class="divTableHead text-center">
                {{ $t('Payment.Table.Headers.TransactionType') }}
              </th>

              <th class="divTableHead text-center">
                {{ $t('Payment.Table.Headers.TransactionResult') }}
              </th>

              <th class="divTableHead text-center">
                {{ $t('Order.OrderDetailsDialog.Transaction.CreatedAt') }}
              </th>

              <th class="divTableHead text-center">
                {{ $t('Order.OrderDetailsDialog.Transaction.ChangedAt') }}
              </th>

              <th class="divTableHead text-right" />
            </template>
          </tr>

          <template v-for="(transaction, index) in transactionsList">
            <tr
              :key="`transaction-${transaction.Id}-${index}`"
              class="divTableRow"
            >
              <td
                :class="{'no-border': transaction.historyVisible}"
                class="divTableCell text-left text-no-wrap"
              >
                <div
                  style="cursor: pointer;"
                  @click="copyToClipboard(transaction.Id)"
                >
                  {{ transaction.Id }}
                </div>

                <div
                  v-if="transaction.TransactionId"
                  class="grey--text"
                  style="cursor: pointer;"
                  @click="copyToClipboard(transaction.TransactionId)"
                >
                  {{ transaction.TransactionId }}
                </div>
              </td>

              <td
                :class="{'no-border': transaction.historyVisible}"
                class="divTableCell text-right text-no-wrap"
              >
                {{ transaction.Amount | currency }}
              </td>

              <td
                :class="{'no-border': transaction.historyVisible}"
                class="divTableCell text-center text-no-wrap"
              >
                <template v-if="transaction?.Payment?.CardType && CardNameEnum.has(transaction.Payment.CardType)">
                  <card-brand-svg-icon
                    :name="transaction.Payment.CardType"
                    class="mt-1"
                    width="26"
                  />
                </template>

                <template v-else>
                  <template v-for="(payment, idx) in paymentTypes">
                    <v-tooltip
                      v-if="transaction.PaymentType === payment.code"
                      :key="`payment-transaction-${index}-${idx}`"
                      :color="payment.color2"
                      right
                    >
                      <template #activator="{on}">
                        <img
                          v-if="!!payment.image"
                          :src="`${appConfig.LOCATION_DATA.CdnUrl}assets/payments/${payment.image}`"
                          height="18"
                          width="auto"
                          v-on="on"
                        >
                        <v-icon
                          v-else
                          :color="payment.color1"
                          class="mx-1"
                          size="20"
                          v-on="on"
                        >
                          {{ payment.code === PaymentTypeEnum.CASH ? appCurrency.Icon : payment.icon }}
                        </v-icon>
                      </template>

                      <span>
                        {{ transaction.Amount | currency }}
                        {{
                          payment.code === PaymentTypeEnum.CASH ? $t('Order.PaymentMethod.Cash').Title.toLocaleUpperCase($i18n.locale) : payment.code === PaymentTypeEnum.CARD ? $t('Order.PaymentMethod.Card').Title.toLocaleUpperCase($i18n.locale) : payment.name.toLocaleUpperCase($i18n.locale)
                        }}
                      </span>
                    </v-tooltip>
                  </template>
                </template>
              </td>

              <template v-if="appHasFiscalDocsEnabled && appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD">
                <td
                  :class="{'no-border': transaction.historyVisible}"
                  class="divTableCell text-left text-no-wrap"
                >
                  <template v-if="transaction.Payment">
                    <div style="line-height: 14px;">
                      <div
                        v-if="!!transaction.Payment.TerminalNickname"
                        class="font-weight-bold"
                      >
                        {{ transaction.Payment.TerminalNickname }}
                      </div>

                      <div
                        v-if="!!getTerminal(transaction.Payment.TerminalVendorId, transaction.Payment.TerminalModelId)"
                        class="grey--text"
                      >
                        {{
                          getTerminal(transaction.Payment.TerminalVendorId, transaction.Payment.TerminalModelId).Manufacturer
                        }} - {{ getTerminal(transaction.Payment.TerminalVendorId, transaction.Payment.TerminalModelId).Model }}
                      </div>

                      <div
                        v-if="!!getVendorAcquirerTextById(transaction.Payment.TerminalVendorId, transaction.Payment.TerminalAcquirerCode)"
                        class="grey--text"
                      >
                        {{ getVendorAcquirerTextById(transaction.Payment.TerminalVendorId, transaction.Payment.TerminalAcquirerCode) }}
                      </div>
                    </div>
                  </template>
                </td>

                <td class="divTableCell text-center text-no-wrap">
                  <template v-if="transaction.Payment">
                    <status-chip
                      v-if="getTransactionType(transaction.Payment.UserConfirmedAt ? 'SALE' : transaction.Payment.TransactionLastType)"
                      :avatar-color="`${getTransactionType(transaction.Payment.UserConfirmedAt ? 'SALE' : transaction.Payment.TransactionLastType).Color} darken-2`"
                      :color="getTransactionType(transaction.Payment.UserConfirmedAt ? 'SALE' : transaction.Payment.TransactionLastType).Color"
                      :icon="getTransactionType(transaction.Payment.UserConfirmedAt ? 'SALE' : transaction.Payment.TransactionLastType).Icon"
                      :label="getTransactionType(transaction.Payment.UserConfirmedAt ? 'SALE' : transaction.Payment.TransactionLastType).Name"
                      :min-width="115"
                      dense
                    />
                  </template>
                </td>

                <td class="divTableCell text-center text-no-wrap">
                  <div
                    v-if="transaction.Payment"
                    class="text-no-wrap"
                  >
                    <status-chip
                      v-if="getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus)"
                      :avatar-color="`${getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus).Color} darken-2`"
                      :color="getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus).Color"
                      :icon="getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus).Icon"
                      :label="getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus).Name"
                      :tooltip="getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus).Tooltip"
                      dense
                      icon-only
                    />

                    <status-chip
                      v-if="getTransactionResult(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult)"
                      :avatar-color="`${getTransactionResult(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult).ColorAvatar} darken-2`"
                      :color="getTransactionResult(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult).Color"
                      :icon="getTransactionResult(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult).Icon"
                      :label="getTransactionResult(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult).Name"
                      :min-width="115"
                      :tooltip="getTransactionResultTooltip(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult, {user: transaction.Payment.User}, transaction.Payment?.Message || '')"
                      dense
                    />
                  </div>
                </td>

                <td class="divTableCell text-right text-no-wrap">
                  <div
                    v-if="transaction.CreatedAt"
                    class="text-no-wrap d-flex align-center justify-center"
                  >
                    <display-date-time
                      :date-time="transaction.CreatedAt"
                      class="mr-2"
                    />

                    <user-chip
                      v-if="transaction.Staff"
                      :color="transaction.StaffColor"
                      :full-name="transaction.Staff"
                      :short-name="transaction.StaffShortName"
                    />
                  </div>
                </td>

                <td class="divTableCell text-right text-no-wrap">
                  <div
                    v-if="transaction.RemovedAt"
                    class="text-no-wrap d-flex align-center justify-center"
                  >
                    <display-date-time
                      :date-time="transaction.RemovedAt"
                      class="mr-2"
                      date-class="red--text"
                    />

                    <user-chip
                      v-if="transaction.RemovedStaff"
                      :color="transaction.RemovedStaffColor"
                      :full-name="transaction.RemovedStaff"
                      :short-name="transaction.RemovedStaffShortName"
                    />
                  </div>
                </td>

                <td class="divTableCell text-right text-no-wrap">
                  <v-btn
                    v-if="appHasFiscalDocsEnabled && appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD && PaymentTypeEnum.isEftPosPaymentType(transaction.PaymentType) && !order?.ExternalPlatform"
                    icon
                    x-small
                    @click="getItemDetails(transaction)"
                  >
                    <v-icon>mdi-history</v-icon>
                  </v-btn>
                </td>
              </template>
            </tr>
          </template>
        </table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'
import PaymentType from '@/mixins/orders/paymentType'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import {getContrastColor} from '@/lib/color/index.js'
import TerminalsCommon from '@/mixins/payments/terminalsCommon.js'
import StatusChip from '@/components/common/StatusChip.vue'
import {splitDateTimeStrToObj} from '@/lib/helper/helper.js'
import CardBrandSvgIcon from '@/components/common/CardBrandSvgIcon.vue'
import CardNameEnum from '@/api/Enums/CardNameEnum.js'
import DisplayDateTime from '@/components/common/DisplayDateTime.vue'
import UserChip from '@/components/common/UserChip.vue'

export default {
  name      : 'OrderPaymentTransactionHistoryTable',
  components: {UserChip, DisplayDateTime, CardBrandSvgIcon, StatusChip},
  directives: {},
  mixins    : [Auth, AppData, PaymentType, TerminalsCommon],
  props     : {
    order: {
      type   : Object,
      default: undefined
    },
    transactions: {
      type   : Array,
      default: () => []
    }
  },
  dataStore: {},
  data() {
    return {
      selectedDetailsItem: null
    }
  },
  computed: {
    CardNameEnum() {
      return CardNameEnum
    },
    FiscalDocsProviderTypeEnum() {
      return FiscalDocsProviderTypeEnum
    },
    PaymentTypeEnum() {
      return PaymentTypeEnum
    },

    transactionsList() {
      return this.transactions || []
    }
  },
  watch: {},
  beforeCreate() {
  },
  created() {
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  destroyed() {
  },
  methods: {
    splitDateTimeStrToObj,
    getContrastColor,

    getItemDetails(item) {
      if (!item) return
      this.selectedItem = item
      window.callAS(window.SocketCommand.Payment.EftPos.Get, {Id: item.Id})
    }
  }
}
</script>

<style scoped>

:deep(.v-speed-dial__list) {
  padding: 0;
}

.divTableCell.no-border {
  border-bottom: 0 !important;
}
</style>
